/*
* === ReviewStep.js (Production - Split-Brain Data Sync & Self-Healing Fix) ===
*/
import React, { useState, useEffect } from 'react';
import { 
    Box, Typography, Grid, Paper, Button, Divider, Alert, CircularProgress, 
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, 
    Chip, FormControl, FormControlLabel, Checkbox, FormHelperText 
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PersonIcon from '@mui/icons-material/Person';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const DetailRow = ({ label, value, isFromProfile = false, isMissing = false }) => (
    <Grid container spacing={2} sx={{ mb: 1.5, alignItems: 'center' }}>
        <Grid item xs={12} sm={4}><Typography variant="body2" color="text.secondary" fontWeight="bold">{label}</Typography></Grid>
        <Grid item xs={12} sm={8} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isMissing ? <Typography variant="body2" color="error.main" fontWeight="bold">Missing Data</Typography> : <Typography variant="body1">{value || 'N/A'}</Typography>}
            {isFromProfile && !isMissing && <Chip icon={<PersonIcon />} label="From Profile" size="small" color="info" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />}
        </Grid>
    </Grid>
);

const ReviewStep = ({ formData, handleInputChange, profileData, errors }) => {
    const { token, user } = useAuth(); // We bring in 'user' as the ultimate source of truth for names
    
    const [profile, setProfile] = useState(null);
    const [loadingProfile, setLoadingProfile] = useState(true);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isSavingProfile, setIsSavingProfile] = useState(false);
    
    const [editForm, setEditForm] = useState({
        idNumber: '', cellNumber: '', gender: '', race: '',
        physicalStreet: '', physicalCity: '', physicalProvince: '', physicalCode: ''
    });

    const fetchProfile = async () => {
        setLoadingProfile(true);
        try {
            const res = await api.get(`/applicant/profile?t=${new Date().getTime()}`, { headers: { Authorization: `Bearer ${token}` } });
            const data = res.data || {};
            setProfile(data);
            
            // Sync form state safely
            setEditForm({
                idNumber: data.IdNumber || formData?.personal?.IdNumber || '',
                cellNumber: data.CellNumber || formData?.personal?.CellNumber || formData?.personal?.cellNo || '',
                gender: data.Gender || formData?.personal?.Gender || '',
                race: data.Race || formData?.personal?.Race || '',
                physicalStreet: data.address?.physicalStreet || formData?.address?.physicalStreet || '',
                physicalCity: data.address?.physicalCity || formData?.address?.physicalCity || '',
                physicalProvince: data.address?.physicalProvince || formData?.address?.physicalProvince || '',
                physicalCode: data.address?.physicalCode || formData?.address?.physicalCode || ''
            });
        } catch (err) {
            console.error("Failed to load profile for review step", err);
        } finally {
            setLoadingProfile(false);
        }
    };

    useEffect(() => { fetchProfile(); }, []);

    const missingFields = [];
    if (!editForm.idNumber) missingFields.push('ID Number');
    if (!editForm.cellNumber) missingFields.push('Cell Number');
    if (!editForm.gender) missingFields.push('Gender');
    if (!editForm.physicalCity) missingFields.push('Physical Address');
    
    const isProfileComplete = missingFields.length === 0;

    const handleSaveProfile = async () => {
        setIsSavingProfile(true);
        try {
            // 🚨 THE MASTER FIX: The Self-Healing Payload
            // We pull the name from the Application Form. If blank, we pull from the Global Profile.
            // If the Global Profile was wiped, we pull from the immutable JWT 'user' context.
            const safeFirstName = formData?.personal?.FirstNames || profile?.FirstNames || user?.FirstNames || user?.firstNames || '';
            const safeSurname = formData?.personal?.Surname || profile?.Surname || user?.Surname || user?.surname || '';
            const safeEmail = formData?.personal?.email || profile?.Email || user?.Email || user?.email || '';

            // We send BOTH casing variations so the backend cannot miss the destructured variables.
            const safePayload = {
                firstNames: safeFirstName, FirstNames: safeFirstName,
                surname: safeSurname, Surname: safeSurname,
                email: safeEmail, Email: safeEmail,
                idNumber: editForm.idNumber, IdNumber: editForm.idNumber,
                cellNumber: editForm.cellNumber, CellNumber: editForm.cellNumber,
                gender: editForm.gender, Gender: editForm.gender,
                race: editForm.race, Race: editForm.race,
                address: {
                    physicalStreet: editForm.physicalStreet,
                    physicalCity: editForm.physicalCity,
                    physicalProvince: editForm.physicalProvince,
                    physicalCode: editForm.physicalCode
                }
            };

            // 1. Force the backend Global Profile to heal
            await api.put('/applicant/profile', safePayload, { headers: { Authorization: `Bearer ${token}` } });
            
            // 2. Force the Application Form JSON to heal synchronously
            if (handleInputChange) {
                handleInputChange('personal', { target: { name: 'FirstNames', value: safeFirstName }});
                handleInputChange('personal', { target: { name: 'Surname', value: safeSurname }});
                handleInputChange('personal', { target: { name: 'email', value: safeEmail }});
                handleInputChange('personal', { target: { name: 'IdNumber', value: editForm.idNumber }});
                handleInputChange('personal', { target: { name: 'CellNumber', value: editForm.cellNumber }});
                handleInputChange('address', { target: { name: 'physicalStreet', value: editForm.physicalStreet }});
                handleInputChange('address', { target: { name: 'physicalCity', value: editForm.physicalCity }});
                handleInputChange('address', { target: { name: 'physicalProvince', value: editForm.physicalProvince }});
                handleInputChange('address', { target: { name: 'physicalCode', value: editForm.physicalCode }});
            }

            await fetchProfile(); 
            setIsEditModalOpen(false);
        } catch (err) {
            console.error("Failed to update profile", err);
            alert("Failed to update profile. Please try again.");
        } finally {
            setIsSavingProfile(false);
        }
    };

    const { personal, address, academic, financial, guardian, jbmarks, motivation, popia } = formData || {};
    
    // UI Display Fallbacks
    const displayFirstName = personal?.FirstNames || profile?.FirstNames || user?.FirstNames || '';
    const displaySurname = personal?.Surname || profile?.Surname || user?.Surname || '';
    const fullName = `${displayFirstName} ${displaySurname}`.trim();

    return (
        <Box sx={{ pb: 2 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>Review Your Application</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Please review all details carefully. Information marked with "From Profile" is securely pulled from your main account settings.
            </Typography>

            {!isProfileComplete && (
                <Alert 
                    severity="error" 
                    icon={<WarningAmberIcon fontSize="inherit" />}
                    action={<Button color="error" size="small" variant="contained" onClick={() => setIsEditModalOpen(true)}>Complete Profile Now</Button>}
                    sx={{ mb: 3, alignItems: 'center' }}
                >
                    <strong>Action Required:</strong> Please complete your profile to finalize your application. Missing: {missingFields.join(', ')}.
                </Alert>
            )}

            {/* --- SECTION 1: Personal Details --- */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" color="primary" fontWeight="bold">Personal Details</Typography>
                    <Button startIcon={<EditIcon />} size="small" onClick={() => setIsEditModalOpen(true)}>Edit Profile</Button>
                </Box>
                <Divider sx={{ mb: 2 }} />
                
                <DetailRow label="Full Name" value={fullName} isFromProfile />
                <DetailRow label="Email Address" value={personal?.email || profile?.Email || user?.Email} />
                <DetailRow label="Phone Number" value={editForm.cellNumber} />
                <DetailRow label="ID Number" value={editForm.idNumber} isFromProfile isMissing={!editForm.idNumber} />
                <DetailRow label="Gender" value={editForm.gender} isFromProfile isMissing={!editForm.gender} />
                <DetailRow label="Race" value={editForm.race} isFromProfile />
                <DetailRow label="T-Shirt Size" value={personal?.tShirtSize} />
                
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>Physical Address</Typography>
                <DetailRow label="Address" value={[editForm.physicalStreet, editForm.physicalCity, editForm.physicalProvince].filter(Boolean).join(', ')} isMissing={!editForm.physicalCity} />
            </Paper>

            {/* --- SECTION 2: Academic Information --- */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Typography variant="h6" color="primary" fontWeight="bold" sx={{ mb: 2 }}>Academic Intention</Typography>
                <Divider sx={{ mb: 2 }} />
                <DetailRow label="Current Status" value={academic?.currentStatus === 'highSchool' ? 'High School' : academic?.currentStatus === 'tertiary' ? 'Tertiary Institution' : 'Not Studying'} />
                <DetailRow label="Intended Level of Study" value={academic?.intendedLevel} />
                <DetailRow label="Qualification Type" value={academic?.intendedQualification} />
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>Primary Choice (Choice 1)</Typography>
                <DetailRow label="Institution" value={academic?.intendedInstitution1 || academic?.intendedInstitution} />
                <DetailRow label="Course" value={academic?.intendedCourse1 || academic?.intendedCourse} />
                
                {academic?.intendedInstitution2 && (
                    <><Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>Choice 2</Typography><DetailRow label="Institution" value={academic?.intendedInstitution2} /><DetailRow label="Course" value={academic?.intendedCourse2} /></>
                )}
                {academic?.intendedInstitution3 && (
                    <><Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>Choice 3</Typography><DetailRow label="Institution" value={academic?.intendedInstitution3} /><DetailRow label="Course" value={academic?.intendedCourse3} /></>
                )}
            </Paper>

            {/* --- SECTION 3: Financial & Household --- */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Typography variant="h6" color="primary" fontWeight="bold" sx={{ mb: 2 }}>Financial Background</Typography>
                <Divider sx={{ mb: 2 }} />
                <DetailRow label="Household Income" value={financial?.householdIncome} />
                <DetailRow label="Number of Dependants" value={financial?.numberOfDependants} />
                <DetailRow label="Requires Accommodation" value={financial?.needsAccommodation} />
                <DetailRow label="Receiving Other Aid" value={financial?.otherAid} />
            </Paper>

            {/* --- SECTION 4: JB Marks Specific Data --- */}
            {jbmarks?.numStatus === 'Yes' && (
                <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2, borderLeft: '4px solid', borderColor: 'secondary.main' }}>
                    <Typography variant="h6" color="secondary" fontWeight="bold" sx={{ mb: 2 }}>NUM Membership Details</Typography>
                    <Divider sx={{ mb: 2 }} />
                    <DetailRow label="NUM Member Status" value={jbmarks?.numStatus} />
                    <DetailRow label="Member Name" value={`${guardian?.firstNames || ''} ${guardian?.surname || ''}`} />
                    <DetailRow label="Member ID" value={guardian?.idNumber} />
                    <DetailRow label="Region" value={jbmarks?.region} />
                    <DetailRow label="Branch" value={jbmarks?.branch} />
                    <DetailRow label="Company Name" value={jbmarks?.companyName} />
                    <DetailRow label="Industry Number" value={jbmarks?.industryNo} />
                </Paper>
            )}

            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Typography variant="h6" color="primary" fontWeight="bold" sx={{ mb: 2 }}>Motivation & Declarations</Typography>
                <Divider sx={{ mb: 2 }} />
                <DetailRow label="Motivation" value={motivation} />
                <Divider sx={{ my: 2 }} />
                <DetailRow label="POPIA Signed By" value={popia?.fullName} />
                <DetailRow label="POPIA Location" value={popia?.location} />
            </Paper>

            {/* --- SECTION 6: Final Declaration Checkbox --- */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: 'action.hover', border: '1px solid', borderColor: errors?.declaration ? 'error.main' : 'divider' }}>
                <Typography variant="h6" color="primary" fontWeight="bold" sx={{ mb: 2 }}>Final Declaration</Typography>
                <FormControl error={!!errors?.declaration}>
                    <FormControlLabel 
                        control={<Checkbox name="declaration" checked={formData?.declaration || false} onChange={(e) => handleInputChange('declaration', { target: { name: 'declaration', value: e.target.checked, type: 'checkbox', checked: e.target.checked }})} />} 
                        label={<Typography variant="body2" fontWeight="bold">I declare that all the information provided in this application is true and correct to the best of my knowledge. I understand that any false information may lead to my application being disqualified.</Typography>}
                    />
                    {errors?.declaration && <FormHelperText>{errors.declaration}</FormHelperText>}
                </FormControl>
            </Paper>

            {/* --- IN-PLACE PROFILE EDIT MODAL --- */}
            <Dialog open={isEditModalOpen} onClose={() => !isSavingProfile && setIsEditModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Update Missing Profile Details</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Please complete these mandatory fields. This information will be permanently saved to your profile.</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="ID/Passport Number" required value={editForm.idNumber} onChange={(e) => setEditForm({...editForm, idNumber: e.target.value})} /></Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="Cell Number" required value={editForm.cellNumber} onChange={(e) => setEditForm({...editForm, cellNumber: e.target.value})} /></Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth select label="Gender" required value={editForm.gender} onChange={(e) => setEditForm({...editForm, gender: e.target.value})}>
                                <MenuItem value="Male">Male</MenuItem><MenuItem value="Female">Female</MenuItem><MenuItem value="Other">Other</MenuItem><MenuItem value="Prefer not to say">Prefer not to say</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth select label="Race (Equity Purposes)" required value={editForm.race} onChange={(e) => setEditForm({...editForm, race: e.target.value})}>
                                <MenuItem value="African">African</MenuItem><MenuItem value="Coloured">Coloured</MenuItem><MenuItem value="Indian">Indian</MenuItem><MenuItem value="White">White</MenuItem><MenuItem value="Other">Other</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12}><Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>Physical Address</Typography><TextField fullWidth label="Street Address" sx={{ mb: 2 }} required value={editForm.physicalStreet} onChange={(e) => setEditForm({...editForm, physicalStreet: e.target.value})} /></Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="City/Town" required value={editForm.physicalCity} onChange={(e) => setEditForm({...editForm, physicalCity: e.target.value})} /></Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="Province" required value={editForm.physicalProvince} onChange={(e) => setEditForm({...editForm, physicalProvince: e.target.value})} /></Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsEditModalOpen(false)} disabled={isSavingProfile}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveProfile} disabled={isSavingProfile}>{isSavingProfile ? 'Saving...' : 'Save & Update Profile'}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ReviewStep;